import React, { useMemo } from 'react';
import loadLowIcon from '../../assets/load-low.png';
import loadNormalIcon from '../../assets/load-normal.png';
import loadHighIcon from '../../assets/load-high.png';
import { formatPercent, getRange } from '../../utils';
import type { AnalysisGroup, LoadRangeKey, PeriodMode } from '../../types';
import styles from './index.module.scss';

const RANGE_CARDS: { key: LoadRangeKey; label: string; range: string; color: string; icon: string }[] = [
  { key: 'low', label: '低负荷', range: '0~60%', color: '#20d9a0', icon: loadLowIcon },
  { key: 'mid', label: '常用负荷', range: '60~80%', color: '#f4c941', icon: loadNormalIcon },
  { key: 'high', label: '较高负荷', range: '80%以上', color: '#ff9046', icon: loadHighIcon },
];

const formatTenThousandHours = (minutes?: number) => {
  if (minutes === undefined || minutes === null) return '--';
  return (Number(minutes) / 60 / 10000).toFixed(1);
};

const formatMinutes = (minutes?: number) => {
  if (minutes === undefined || minutes === null) return '--';
  return Math.round(Number(minutes)).toString();
};

const formatOneDecimal = (value?: number) => {
  if (value === undefined || value === null) return '--';
  return Number(value).toFixed(1);
};

interface ResilienceSummaryProps {
  groups: AnalysisGroup[];
  periodMode: PeriodMode;
}

const ResilienceSummary: React.FC<ResilienceSummaryProps> = ({ groups, periodMode }) => {
  const rows = useMemo(() => groups.flatMap(group => group.rows), [groups]);
  const durationLabel = periodMode === 'day' ? '今日累计：' : periodMode === 'month' ? '本月累计：' : '全年累计：';
  const durationValue = (minutes?: number) =>
    periodMode === 'day' ? formatMinutes(minutes) : formatTenThousandHours(minutes);
  const durationUnit = periodMode === 'day' ? '分钟' : '万小时';

  return (
    <div className={styles.summary}>
      {RANGE_CARDS.map(card => {
        const rangeValues = rows.map(row => getRange(row, card.key)).filter(Boolean);
        const duration = rangeValues.length
          ? rangeValues.reduce((total, item) => total + Number(item?.duration ?? 0), 0)
          : undefined;
        const ratio = rangeValues.length
          ? rangeValues.reduce((total, item) => total + Number(item?.durationPercent ?? 0), 0) / rangeValues.length
          : undefined;
        const rowsWithAvgLoad = rows.filter(row => row.avgLoadRate !== undefined && row.avgLoadRate !== null);
        const avgLoad = rowsWithAvgLoad.length
          ? rowsWithAvgLoad.reduce((total, row) => total + Number(row.avgLoadRate), 0) / rowsWithAvgLoad.length
          : undefined;

        return (
          <div key={card.key} className={styles.card} style={{ '--range-color': card.color } as React.CSSProperties}>
            <div className={styles.rangeName}>
              <img src={card.icon} alt="" />
              <span>
                {card.label} {card.range}：
              </span>
            </div>
            <div className={styles.progress}>
              <span className={styles.progressTrack}>
                <span className={styles.progressBar} style={{ width: ratio ? formatPercent(ratio) : '0%' }} />
              </span>
              <span className={styles.progressValue}>{formatPercent(ratio)}</span>
            </div>
            <div className={styles.metric}>
              <span>{durationLabel}</span>
              <span className={styles.metricValue}>{durationValue(duration)}</span>
              <span>{durationUnit}</span>
            </div>
            <div className={styles.metric}>
              <span>平均每台主变每天:</span>
              <span className={styles.metricValue}>{formatOneDecimal(avgLoad)}</span>
              <span>小时</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ResilienceSummary;
